
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Row, Col, Card, Alert, InputGroup } from 'react-bootstrap';
import { FaUser, FaEnvelope, FaLock, FaCamera, FaSave, FaEye, FaEyeSlash } from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';

function Profile() {
    const navigate = useNavigate();
    const [userInfo, setUserInfo] = useState({
        name: '',
        email: '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        view_currency: 'AED',
    });
    const [alert, setAlert] = useState(null);
    const [errors, setErrors] = useState({});
    const [isUpdating, setIsUpdating] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    useEffect(() => {
        const userInfoFromStorage = localStorage.getItem('user-info');
        if (userInfoFromStorage) {
            const data = JSON.parse(userInfoFromStorage);
            setUserInfo(prev => ({
                ...prev,
                ...data.user,
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
                view_currency: data.user.view_currency || 'AED',
            }));
        }
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setUserInfo({ ...userInfo, [name]: value });
        setErrors({ ...errors, [name]: null });
    };

    const toggleVisibility = (field) => {
        if (field === 'current') setShowCurrentPassword(!showCurrentPassword);
        if (field === 'new') setShowNewPassword(!showNewPassword);
        if (field === 'confirm') setShowConfirmPassword(!showConfirmPassword);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsUpdating(true);
        
        // Client-side validation
        if (userInfo.newPassword && userInfo.newPassword !== userInfo.confirmPassword) {
            setAlert({ type: 'danger', message: 'New passwords do not match.' });
            setIsUpdating(false);
            return;
        }

        // Prepare data for update
        const updateData = { 
            name: userInfo.name,
            email: userInfo.email,
            view_currency: userInfo.view_currency,
        };

        if (userInfo.newPassword) {
            updateData.current_password = userInfo.currentPassword;
            updateData.password = userInfo.newPassword;
            updateData.password_confirmation = userInfo.confirmPassword;
        }

        const userInfoFromStorage = localStorage.getItem('user-info');
        const token = JSON.parse(userInfoFromStorage)?.token;
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const origin = localStorage.getItem('user-origin');

        try {
            const response = await fetch(`${backendUrl}/api/user/update`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                body: JSON.stringify(updateData),
            });

            if (response.ok) {
                const updatedResponse = await response.json();
                const currentData = JSON.parse(localStorage.getItem('user-info'));

                currentData.user = updatedResponse.user;
                localStorage.setItem('user-info', JSON.stringify(currentData));

                // Notify same-tab components (e.g. dashboards) that currency may have changed
                window.dispatchEvent(new Event('currencyChanged'));
                
                setAlert({ type: 'success', message: 'Profile updated successfully!' });
                setUserInfo(prev => ({ 
                    ...prev, 
                    currentPassword: '', 
                    newPassword: '', 
                    confirmPassword: '' 
                })); 
                setShowCurrentPassword(false);
                setShowNewPassword(false);
                setShowConfirmPassword(false);
                
                setTimeout(() => {
                    setAlert(null);
                }, 4000);
            } else if (response.status === 422) {
                const errorData = await response.json();
                setErrors(errorData.errors || {});
                setAlert({ type: 'danger', message: 'Please correct the highlighted errors.' });
            } else {
                throw new Error('Failed to update profile');
            }
        } catch (error) {
            setAlert({ type: 'danger', message: 'Failed to update profile. Please try again later.' });
            console.error(error);
        } finally {
            setIsUpdating(false);
        }
    };

    const getInitials = (name) => {
        if (!name) return 'U';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    };

    // Decorative Doodle Components
    const Doodles = () => (
        <>
            {/* Top Left Circle */}
            <svg style={{ position: 'absolute', top: '-15px', left: '-15px', opacity: 0.12 }} width="120" height="120">
                <circle cx="60" cy="60" r="50" fill="#4e54c8" />
                <circle cx="60" cy="60" r="35" fill="none" stroke="#4e54c8" strokeWidth="1" strokeDasharray="5,5" />
            </svg>
            {/* Top Right Dots */}
            <svg style={{ position: 'absolute', top: '25px', right: '30px', opacity: 0.15 }} width="80" height="80" viewBox="0 0 100 100">
                {[...Array(3)].map((_, i) => 
                    [...Array(3)].map((_, j) => (
                        <circle key={`${i}-${j}`} cx={20 + i*30} cy={20 + j*30} r="6" fill="#4e54c8" />
                    ))
                )}
            </svg>
            {/* Bottom Right Wave */}
            <svg style={{ position: 'absolute', bottom: '-10px', right: '0', opacity: 0.18 }} width="180" height="80" viewBox="0 0 200 100">
                <path d="M0 80 Q 50 10, 100 80 T 200 80" fill="transparent" stroke="#4e54c8" strokeWidth="5" />
                <path d="M0 60 Q 50 -10, 100 60 T 200 60" fill="transparent" stroke="#8f94fb" strokeWidth="3" />
            </svg>
            {/* Blue Accent Shapes */}
            <div style={{ 
                position: 'absolute', 
                top: '30%', 
                left: '15%', 
                width: '40px', 
                height: '40px', 
                backgroundColor: 'rgba(78, 84, 200, 0.08)', 
                transform: 'rotate(45deg)',
                border: '2px solid rgba(78, 84, 200, 0.15)',
                borderRadius: '8px'
            }}></div>
            <div style={{ 
                position: 'absolute', 
                top: '55%', 
                left: '8%', 
                width: '20px', 
                height: '20px', 
                border: '2px solid rgba(78, 84, 200, 0.2)',
                borderRadius: '50%'
            }}></div>
        </>
    );

    const CardDoodles = () => (
        <div style={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            width: '100%', 
            height: '100%', 
            pointerEvents: 'none', 
            zIndex: 0,
            overflow: 'hidden'
        }}>
            {/* Subtle blue blob 1 */}
            <div style={{ 
                position: 'absolute', 
                top: '150px', 
                right: '-80px', 
                width: '300px', 
                height: '300px', 
                borderRadius: '50%', 
                background: 'radial-gradient(circle, rgba(78, 84, 200, 0.08) 0%, rgba(255,255,255,0) 70%)' 
            }}></div>
            {/* Subtle blue blob 2 */}
            <div style={{ 
                position: 'absolute', 
                bottom: '15%', 
                left: '-50px', 
                width: '250px', 
                height: '250px', 
                borderRadius: '50%', 
                background: 'radial-gradient(circle, rgba(143, 148, 251, 0.08) 0%, rgba(255,255,255,0) 70%)' 
            }}></div>
            {/* Blue Crosses and Pluses */}
            <div style={{ position: 'absolute', top: '15%', right: '8%', opacity: 0.15, color: '#4e54c8', fontSize: '24px', fontWeight: 'bold' }}>×</div>
            <div style={{ position: 'absolute', top: '22%', right: '15%', opacity: 0.12, color: '#4e54c8', fontSize: '18px' }}>×</div>
            <div style={{ position: 'absolute', bottom: '25%', left: '5%', opacity: 0.15, color: '#4e54c8', fontSize: '28px', fontWeight: 'bold' }}>+</div>
            <div style={{ position: 'absolute', top: '45%', left: '2%', opacity: 0.1, color: '#4e54c8', fontSize: '20px' }}>○</div>
            <div style={{ position: 'absolute', bottom: '10%', right: '12%', opacity: 0.12, color: '#4e54c8', fontSize: '22px' }}>⬡</div>
        </div>
    );

    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid">
                    {/* Header Section */}
                    <div className="row">
                        <div className="col-12">
                            <div className="page-title-box d-flex align-items-center justify-content-between">
                                <h4 className="mb-0 font-size-18">My Profile</h4>
                                <ol className="breadcrumb mb-0">
                                    <li className="breadcrumb-item"><a href="/dashboard">Dashboard</a></li>
                                    <li className="breadcrumb-item active">Profile</li>
                                </ol>
                            </div>
                        </div>
                    </div>

                    <Row className="justify-content-center mt-4">
                        <Col xl={8} lg={10}>
                            <Card className="border-0 shadow-sm overflow-hidden position-relative" style={{ borderRadius: '15px' }}>
                                <CardDoodles />
                                
                                <div className="profile-header-bg overflow-hidden" style={{ 
                                    height: '120px', 
                                    background: 'linear-gradient(135deg, #ffffff 0%, #f7faff 100%)',
                                    position: 'relative',
                                    borderBottom: '1px solid #f0f4f8'
                                }}>
                                    <Doodles />
                                </div>
                                
                                <Card.Body className="pt-0 position-relative" style={{ zIndex: 1 }}>
                                    <div className="text-center" style={{ marginTop: '-60px' }}>
                                        <div className="position-relative d-inline-block mb-3">
                                            <div className="rounded-circle border border-4 border-white shadow-sm d-flex align-items-center justify-content-center" 
                                                 style={{ 
                                                     width: '120px', 
                                                     height: '120px', 
                                                     fontSize: '40px', 
                                                     fontWeight: 'bold', 
                                                     color: '#4e54c8', 
                                                     background: 'linear-gradient(135deg, #f0f7ff 0%, #ffffff 100%)',
                                                     position: 'relative', 
                                                     zIndex: 2 
                                                 }}>
                                                {getInitials(userInfo.name)}
                                            </div>
                                            <div className="position-absolute bottom-0 end-0 bg-white rounded-circle p-2 shadow-sm border border-2 border-primary" 
                                                 style={{ cursor: 'pointer', zIndex: 3 }}>
                                                <FaCamera size={14} className="text-primary" />
                                            </div>
                                        </div>
                                        <h4 className="mb-1 font-weight-bold" style={{ color: '#333' }}>{userInfo.name || 'User'}</h4>
                                        <p className="text-muted mb-4">{userInfo.email}</p>
                                    </div>

                                    {alert && (
                                        <Alert variant={alert.type} className="border-0 shadow-sm mx-3" dismissible onClose={() => setAlert(null)}>
                                            <div className="d-flex align-items-center">
                                                {alert.type === 'success' ? '✅ ' : '❌ '}
                                                {alert.message}
                                            </div>
                                        </Alert>
                                    )}

                                    <Form onSubmit={handleSubmit} className="p-3">
                                        <Row>
                                            <Col md={6}>
                                                <Form.Group className="mb-4">
                                                    <Form.Label className="fw-semibold">Username</Form.Label>
                                                    <InputGroup hasValidation>
                                                        <InputGroup.Text className="bg-light border-0">
                                                            <FaUser className="text-muted" />
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            placeholder="Enter your name"
                                                            name="name"
                                                            className="bg-light border-0"
                                                            style={{ padding: '10px 15px' }}
                                                            value={userInfo.name}
                                                            onChange={handleChange}
                                                            isInvalid={!!errors.name}
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            {errors.name}
                                                        </Form.Control.Feedback>
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group className="mb-4">
                                                    <Form.Label className="fw-semibold">Email Address</Form.Label>
                                                    <InputGroup hasValidation>
                                                        <InputGroup.Text className="bg-light border-0">
                                                            <FaEnvelope className="text-muted" />
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="email"
                                                            placeholder="Enter your email"
                                                            name="email"
                                                            className="bg-light border-0"
                                                            style={{ padding: '10px 15px' }}
                                                            value={userInfo.email}
                                                            onChange={handleChange}
                                                            isInvalid={!!errors.email}
                                                            disabled // Usually email editing is restricted or requires verification
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            {errors.email}
                                                        </Form.Control.Feedback>
                                                    </InputGroup>
                                                    <small className="text-muted ps-2">Email cannot be changed.</small>
                                                </Form.Group>
                                            </Col>

                                            <Col md={12}>
                                                <hr className="my-4 opacity-50" />
                                                <h5 className="mb-4 text-primary d-flex align-items-center gap-2">
                                                    <i className="mdi mdi-currency-usd"></i> Regional & Currency Settings
                                                </h5>
                                                <Row>
                                                    <Col md={6}>
                                                        <Form.Group className="mb-4">
                                                            <Form.Label className="fw-semibold">Default Viewing Currency</Form.Label>
                                                            <Form.Select
                                                                name="view_currency"
                                                                className="bg-light border-0"
                                                                style={{ padding: '10px 15px' }}
                                                                value={userInfo.view_currency}
                                                                onChange={handleChange}
                                                            >
                                                                <option value="AED">AED</option>
                                                                <option value="USD">USD</option>
                                                            </Form.Select>
                                                        </Form.Group>
                                                    </Col>
                                                </Row>
                                            </Col>

                                            <Col md={12}>
                                                <hr className="my-4 opacity-50" />
                                                <h5 className="mb-4 text-primary d-flex align-items-center gap-2">
                                                    <FaLock size={16} /> Security & Password
                                                </h5>
                                                
                                                <Row>
                                                    <Col md={12}>
                                                        <Form.Group className="mb-4">
                                                            <Form.Label className="fw-semibold small text-muted text-uppercase">Current Password</Form.Label>
                                                            <InputGroup hasValidation>
                                                                <Form.Control
                                                                    type={showCurrentPassword ? 'text' : 'password'}
                                                                    placeholder="Current password"
                                                                    name="currentPassword"
                                                                    className="bg-white border-bottom border-0 rounded-0"
                                                                    style={{ padding: '10px 15px', borderBottom: '1px solid #e1e8f0 !important' }}
                                                                    value={userInfo.currentPassword}
                                                                    onChange={handleChange}
                                                                    isInvalid={!!errors.current_password}
                                                                />
                                                                <Button 
                                                                    variant="link" 
                                                                    className="border-0 bg-transparent text-muted px-3"
                                                                    onClick={() => toggleVisibility('current')}
                                                                    style={{ textDecoration: 'none' }}
                                                                >
                                                                    {showCurrentPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                                                                </Button>
                                                                <Form.Control.Feedback type="invalid">
                                                                    {errors.current_password}
                                                                </Form.Control.Feedback>
                                                            </InputGroup>
                                                        </Form.Group>
                                                    </Col>

                                                    <Col md={12}>
                                                        <Form.Group className="mb-4">
                                                            <Form.Label className="fw-semibold small text-muted text-uppercase">New Password</Form.Label>
                                                            <InputGroup hasValidation>
                                                                <Form.Control
                                                                    type={showNewPassword ? 'text' : 'password'}
                                                                    placeholder="New password"
                                                                    name="newPassword"
                                                                    className="bg-white border-bottom border-0 rounded-0"
                                                                    style={{ padding: '10px 15px', borderBottom: '1px solid #e1e8f0 !important' }}
                                                                    value={userInfo.newPassword}
                                                                    onChange={handleChange}
                                                                    isInvalid={!!errors.password}
                                                                />
                                                                <Button 
                                                                    variant="link" 
                                                                    className="border-0 bg-transparent text-muted px-3"
                                                                    onClick={() => toggleVisibility('new')}
                                                                    style={{ textDecoration: 'none' }}
                                                                >
                                                                    {showNewPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                                                                </Button>
                                                                <Form.Control.Feedback type="invalid">
                                                                    {errors.password}
                                                                </Form.Control.Feedback>
                                                            </InputGroup>
                                                        </Form.Group>
                                                    </Col>

                                                    <Col md={12}>
                                                        <Form.Group className="mb-4">
                                                            <Form.Label className="fw-semibold small text-muted text-uppercase">Confirm Password</Form.Label>
                                                            <InputGroup hasValidation>
                                                                <Form.Control
                                                                    type={showConfirmPassword ? 'text' : 'password'}
                                                                    placeholder="Confirm password"
                                                                    name="confirmPassword"
                                                                    className="bg-white border-bottom border-0 rounded-0"
                                                                    style={{ padding: '10px 15px', borderBottom: '1px solid #e1e8f0 !important' }}
                                                                    value={userInfo.confirmPassword}
                                                                    onChange={handleChange}
                                                                    isInvalid={!!errors.password_confirmation}
                                                                />
                                                                <Button 
                                                                    variant="link" 
                                                                    className="border-0 bg-transparent text-muted px-3"
                                                                    onClick={() => toggleVisibility('confirm')}
                                                                    style={{ textDecoration: 'none' }}
                                                                >
                                                                    {showConfirmPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                                                                </Button>
                                                                <Form.Control.Feedback type="invalid">
                                                                    {errors.password_confirmation}
                                                                </Form.Control.Feedback>
                                                            </InputGroup>
                                                        </Form.Group>
                                                    </Col>
                                                </Row>
                                                <small className="text-muted d-block text-center mt-2">
                                                    Leave blank if you don't want to change your password.
                                                </small>
                                            </Col>
                                        </Row>

                                        <div className="d-flex justify-content-end mt-2 pt-3 border-top position-relative" style={{ zIndex: 5 }}>
                                            <Button 
                                                variant="primary" 
                                                type="submit" 
                                                className="px-4 py-2 d-flex align-items-center gap-2 shadow-sm"
                                                style={{ borderRadius: '8px', transition: 'all 0.3s' }}
                                                disabled={isUpdating}
                                            >
                                                {isUpdating ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                        Updating...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FaSave /> Save Changes
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </Form>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </div>
        </div>
    );
}

export default Profile;




